import { useEffect, useState } from "react";

interface LoadingStep {
  label: string;
  detail: string;
  icon: string;
}

const STEPS: LoadingStep[] = [
  { label: "Scraping listing", detail: "Extracting job metadata from the page", icon: "📡" },
  { label: "Parsing content", detail: "Analyzing description, requirements, salary data", icon: "📄" },
  { label: "Background check", detail: "Searching company reviews & web presence", icon: "🔍" },
  { label: "Hiring intel", detail: "Checking for layoffs, freezes & ghost job reports", icon: "📊" },
  { label: "AI analysis", detail: "Running ghost detection model on all signals", icon: "🤖" },
  { label: "Generating verdict", detail: "Compiling investigation report", icon: "📋" },
];

export function LoadingState() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((i) => Math.min(i + 1, STEPS.length - 1));
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen min-h-dvh flex flex-col items-center justify-center px-4 sm:px-6">
      <div className="w-full max-w-sm">
        {/* Ghost */}
        <div className="relative select-none text-center mb-8 sm:mb-10">
          <div className="text-6xl sm:text-7xl animate-float">👻</div>
          <div
            className="absolute inset-0 flex items-center justify-center text-6xl sm:text-7xl blur-3xl"
            style={{ opacity: 0.1 }}
            aria-hidden
          >
            👻
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6 sm:mb-8">
          <h2
            className="text-base sm:text-lg font-medium text-white mb-1"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Investigating…
          </h2>
          <p className="mono-label">Running full analysis</p>
        </div>

        {/* Steps */}
        <div className="space-y-1">
          {STEPS.map((step, i) => {
            const isActive = i === activeStep;
            const isDone = i < activeStep;
            const isPending = i > activeStep;

            return (
              <div
                key={step.label}
                className="flex items-center gap-2.5 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl transition-all duration-500"
                style={{
                  background: isActive
                    ? "rgba(129,140,248,0.06)"
                    : "transparent",
                  border: isActive
                    ? "1px solid rgba(129,140,248,0.12)"
                    : "1px solid transparent",
                  opacity: isPending ? 0.3 : 1,
                }}
              >
                {/* Status indicator */}
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  {isDone ? (
                    <span className="text-xs" style={{ color: "var(--real-green)" }}>✓</span>
                  ) : isActive ? (
                    <span className="text-sm">{step.icon}</span>
                  ) : (
                    <div
                      className="w-1 h-1 rounded-full"
                      style={{ background: "rgba(255,255,255,0.15)" }}
                    />
                  )}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div
                    className="font-medium truncate"
                    style={{
                      color: isDone
                        ? "var(--text-secondary)"
                        : isActive
                        ? "var(--text-primary)"
                        : "var(--text-tertiary)",
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.65rem",
                    }}
                  >
                    {step.label}
                  </div>
                  {isActive && (
                    <div
                      className="text-xs mt-0.5 animate-fade-in truncate"
                      style={{ color: "var(--text-tertiary)", fontSize: "0.7rem" }}
                    >
                      {step.detail}
                    </div>
                  )}
                </div>

                {/* Scanning indicator */}
                {isActive && (
                  <div
                    className="w-10 sm:w-12 h-0.5 rounded-full overflow-hidden shrink-0"
                    style={{ background: "rgba(255,255,255,0.06)" }}
                  >
                    <div
                      className="h-full rounded-full animate-scanning"
                      style={{
                        width: "40%",
                        background: "linear-gradient(90deg, transparent, var(--investigate), transparent)",
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
